import { checkLocationSuccess } from '@common/redux/slice/app';
import { settleWithTimeout } from '@common/utils/settleWithTimeout';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

/**
 * How long the app shell waits for a position before rendering without one.
 *
 * `navigator.geolocation.getCurrentPosition` invokes NEITHER callback while the
 * browser's permission prompt is still on screen, and its own `timeout` option
 * is specified to start counting only once permission has been granted - so it
 * does not cover an unanswered prompt. On Windows the prompt is shown on the
 * first visit and stays until the visitor clicks; macOS usually answers it from
 * a remembered decision, which is why the hang only reproduced on Windows and
 * only before a reload (MIK-5008).
 *
 * The app shell must never depend on an answer that may never come, so the wait
 * is bounded here and the app renders either way. A position that arrives later
 * is still dispatched, so the map picks it up on its own.
 */
const LOCATION_TIMEOUT_MS = 10000;

const requestPosition = (): Promise<GeolocationPosition> =>
  new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('Geolocation is unavailable'));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      // Belt and braces: honoured by browsers that start the clock before the
      // prompt. `settleWithTimeout` is what actually guarantees the bound.
      timeout: LOCATION_TIMEOUT_MS,
    });
  });

const useMapLocation = ({ isUseMap }: { isUseMap: boolean }) => {
  const dispatch = useDispatch();
  const [initializedMap, setInitializedMap] = useState<{
    isReady: boolean;
    position: any;
    error: any;
  }>({
    isReady: false,
    position: null,
    error: null,
  });
  const status = useMemo(() => isUseMap, [isUseMap]);

  useEffect(() => {
    //realtime location
    if (!status) {
      setInitializedMap((pre) => ({ ...pre, isReady: true }));
      return;
    }

    let cancelled = false;
    setInitializedMap((pre) => ({ ...pre, isReady: false }));

    (async () => {
      const outcome = await settleWithTimeout(
        requestPosition(),
        LOCATION_TIMEOUT_MS
      );
      if (cancelled) return;

      const coords =
        outcome.status === 'fulfilled' ? outcome.value?.coords : undefined;

      if (coords) {
        const position = { lat: coords.latitude, lng: coords.longitude };
        dispatch(checkLocationSuccess(position));
        setInitializedMap((pre) => ({
          ...pre,
          isReady: true,
          position,
          error: null,
        }));
        return;
      }

      // Every remaining path still releases the shell: a position is a nice to
      // have for the map, never a precondition for showing the app.
      const error =
        outcome.status === 'timeout'
          ? `Geolocation did not answer within ${LOCATION_TIMEOUT_MS}ms`
          : outcome.status === 'rejected'
          ? (outcome.reason as any)?.message ?? outcome.reason
          : 'Geolocation returned no coordinates';

      // eslint-disable-next-line no-console
      console.warn('[useLocation] rendering without a position:', error);

      setInitializedMap((pre) => ({ ...pre, isReady: true, error }));
    })();

    return () => {
      cancelled = true;
    };
  }, [status, dispatch]);

  return initializedMap;
};

export default useMapLocation;
